import { Component, inject, input, linkedSignal, signal } from "@angular/core";
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from "@angular/forms";
import { PopoverDirective } from "@kno2/common/directives";
import { NotificationService } from "@kno2/common/services/notifications";
import { AccountService, ChangePasswordRequest } from "@kno2/data-access/account";
import { FormInputComponent } from "@kno2/shared/forms";
import { NotificationAlertComponent } from "@kno2/shared/notification-alert";

@Component({
    selector: "kno2-profile-edit",
    imports: [FormsModule, ReactiveFormsModule, FormInputComponent, NotificationAlertComponent, PopoverDirective],
    templateUrl: "./profile-edit.component.html",
    styles: ``
})
export class ProfileEditComponent {
    private readonly accountService = inject(AccountService);
    private readonly notificationService = inject(NotificationService);

    public parentForm = input.required<FormGroup>();
    public mfaRequired = input.required<boolean>();
    public changingPassword = input<boolean>(false);

    protected readonly error = signal<string>(null);
    protected readonly errorMessages = signal<string[]>([]);
    protected readonly showChangePassword = linkedSignal(this.changingPassword);

    protected passwordForm = new FormGroup(
        {
            oldPassword: new FormControl<string>("", Validators.required),
            password: new FormControl<string>("", Validators.required),
            confirmPassword: new FormControl<string>("", Validators.required)
        },
        {
            updateOn: "submit",
            validators: [this.validatePasswordMatch]
        }
    );
    protected readonly passwordFormSubmitted = signal(false);

    protected mfaTooltip = `
        Two-Factor Authentication adds another level of security to your account. This uses two different authentication factors, something you know and your \
        phone. If your password was stolen, your account would not be able to be accessed unless your phone had also been stolen. <br /><br />\
        <strong>To enable:</strong><ol><li>Enter your password</li><li>Next a code will be sent to your phone via phone call or text</li>\
        <li>Enter the code into the website</li></ol>\
        Upon entering the correct code, you will be granted access to your account.`;
    protected mfaEnforcedTooltip = `Two-Factor Authentication has been enforced for this Organization by your System Administrator.`;

    public async changePassword(): Promise<void> {
        try {
            this.passwordFormSubmitted.set(true);

            if (this.passwordForm.invalid) return;

            await this.accountService.changePassword(<ChangePasswordRequest>this.passwordForm.value);
            this.notificationService.success("Password changed successfully.");
            this.resetChangePasswordForm();
        } catch (err) {
            const { error } = err;
            this.error.set(error.message);
            this.errorMessages.set(Object.values(error.modelState));
        }
    }

    public validatePasswordMatch(control: AbstractControl): ValidationErrors | null {
        const password = control.get("password").value;
        const confirmPassword = control.get("confirmPassword").value;
        return password !== confirmPassword ? { passwordMatch: true } : null;
    }

    private resetChangePasswordForm(): void {
        this.passwordForm.reset();
        this.showChangePassword.set(false);
        this.passwordFormSubmitted.set(false);
        this.error.set(null);
        this.errorMessages.set([]);
    }
}
